// src/features/auth/signup/components/DuplicateCheckField.tsx

import React from 'react';

import type {
  DuplicateStatus,
} from '../types/signup.types';

interface DuplicateCheckFieldProps {
  id: string;

  label: string;

  value: string;

  placeholder: string;

  status: DuplicateStatus;

  message?: string;

  disabled?: boolean;

  maxLength?: number;

  autoComplete?: string;

  inputClassName: string;

  labelClassName: string;

  helperText?: string;

  onChange: (
    value: string,
  ) => void;

  onCheck: () => void;
}

export default function DuplicateCheckField({
  id,
  label,
  value,
  placeholder,
  status,
  message,

  disabled = false,

  maxLength,
  autoComplete,

  inputClassName,
  labelClassName,

  helperText,

  onChange,
  onCheck,
}: DuplicateCheckFieldProps) {
  const isChecking =
    status === 'checking';

  const isAvailable =
    status === 'available';

  const getButtonText = () => {
    switch (status) {
      case 'checking':
        return '확인 중';

      case 'available':
        return '확인 완료';

      case 'duplicate':
        return '재확인';

      default:
        return '중복확인';
    }
  };

  const getMessageClassName =
    (): string => {
      const base =
        'mt-1.5 text-xs sm:mt-2 sm:text-sm';

      if (isAvailable) {
        return `${base} text-emerald-600`;
      }

      if (
        status === 'duplicate'
      ) {
        return `${base} text-red-600`;
      }

      return `${base} text-gray-500`;
    };

  return (
    <div>
      <label
        htmlFor={id}
        className={labelClassName}
      >
        {label}

        <span className="ml-1 text-red-500">
          *
        </span>
      </label>

      <div className="flex w-full items-stretch gap-2">
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          maxLength={maxLength}
          disabled={disabled}
          onChange={(event) =>
            onChange(
              event.target.value,
            )
          }
          className={inputClassName}
        />

        <button
          type="button"
          disabled={
            disabled ||
            isChecking
          }
          onClick={onCheck}
          className={`
            flex min-h-11 w-24 shrink-0
            items-center justify-center
            whitespace-nowrap rounded-lg
            border px-2 py-2.5
            text-xs font-semibold transition

            sm:min-h-12 sm:w-28
            sm:rounded-xl sm:px-3
            sm:py-3 sm:text-sm

            ${
              isAvailable
                ? `
                  border-emerald-500
                  bg-emerald-50
                  text-emerald-700
                `
                : `
                  border-gray-300
                  bg-gray-100
                  text-gray-700

                  hover:border-main
                  hover:bg-main/5
                  hover:text-main
                `
            }

            disabled:cursor-not-allowed
            disabled:opacity-60
          `}
        >
          {isChecking && (
            <span
              className="
                mr-1.5 h-3.5 w-3.5
                animate-spin rounded-full
                border-2 border-gray-400
                border-t-transparent
                sm:h-4 sm:w-4
              "
            />
          )}

          {isAvailable && (
            <svg
              className="mr-1 h-3.5 w-3.5 sm:h-4 sm:w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.704 5.292a1 1 0 010 1.416l-8 8a1 1 0 01-1.416 0l-4-4a1 1 0 011.416-1.416L8 12.586l7.296-7.294a1 1 0 011.408 0z"
              />
            </svg>
          )}

          {getButtonText()}
        </button>
      </div>

      {message ? (
        <p
          className={getMessageClassName()}
        >
          {message}
        </p>
      ) : (
        helperText && (
          <p className="mt-1.5 text-xs text-gray-400 sm:mt-2">
            {helperText}
          </p>
        )
      )}
    </div>
  );
}